export type ComposerAttachment = {
  filename: string
  mime_type: string
  size_bytes: number
  content_b64: string
}

const MAX_FILE_BYTES = 5 * 1024 * 1024
const MAX_ATTACHMENTS = 3
const IMAGE_PREFIX = 'image/'

// Discriminated error so the util stays presentation-agnostic; callers map it
// to a user-facing string via attachmentErrorMessage(). Crossroads has no i18n
// layer (no react-i18next), so messages are plain English strings.
export type AttachmentError =
  | { code: 'not_image' }
  | { code: 'too_many'; max: number }
  | { code: 'too_large'; filename: string }

export type FilesToAttachmentsResult = {
  attachments: ComposerAttachment[]
  error?: AttachmentError
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const raw = reader.result as string
      const b64 = raw.split(',')[1]
      if (b64 === undefined) {
        reject(new Error('Unexpected FileReader result format'))
        return
      }
      resolve(b64)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

// Convert picked/dropped files into ComposerAttachments, enforcing the
// image-only / count / size limits shared by the file picker and the drop zone.
// `existing` is the current attachment list so the count cap accounts for it.
// Duplicate filenames (against existing or within the batch) are silently skipped.
export async function filesToAttachments(
  files: File[],
  existing: ComposerAttachment[],
): Promise<FilesToAttachmentsResult> {
  const images = files.filter((f) => f.type.startsWith(IMAGE_PREFIX))
  if (images.length === 0) {
    return { attachments: [], error: { code: 'not_image' } }
  }

  // Deduplicate: keep the first occurrence; skip filenames already in `existing`
  // or seen earlier in this batch.
  const existingNames = new Set(existing.map((a) => a.filename))
  const seenInBatch = new Set<string>()
  const dedupedImages: File[] = []
  for (const f of images) {
    if (!existingNames.has(f.name) && !seenInBatch.has(f.name)) {
      seenInBatch.add(f.name)
      dedupedImages.push(f)
    }
  }

  // All images were duplicates — harmless no-op, return empty with no error.
  if (dedupedImages.length === 0) {
    return { attachments: [] }
  }

  if (existing.length + dedupedImages.length > MAX_ATTACHMENTS) {
    return {
      attachments: [],
      error: { code: 'too_many', max: MAX_ATTACHMENTS },
    }
  }
  const next: ComposerAttachment[] = []
  for (const f of dedupedImages) {
    if (f.size > MAX_FILE_BYTES) {
      return { attachments: [], error: { code: 'too_large', filename: f.name } }
    }
    const content_b64 = await fileToBase64(f)
    next.push({
      filename: f.name,
      mime_type: f.type,
      size_bytes: f.size,
      content_b64,
    })
  }
  return { attachments: next }
}

export function attachmentErrorMessage(err: AttachmentError): string {
  switch (err.code) {
    case 'not_image':
      return 'Only image files can be attached'
    case 'too_many':
      return `At most ${err.max} attachments per message`
    case 'too_large':
      return `${err.filename} is too large (max 5MB)`
  }
}
